import React, { useState, useMemo, useEffect } from 'react';
import { Form, Table, Pagination, InputGroup, Button, Badge } from 'react-bootstrap';
import { FaSearch, FaEdit, FaEye, FaTrash, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import '../../assets/style/DataTable.css';

const DataTable = ({
    columns,
    data,
    actions,
    searchable = true,
    searchTerm = '',
    onSearchChange,
    pagination = true,
    pageSize = 10,
    striped = true,
    hover = true,
    bordered = false,
    responsive = true,
    className = '',
    onPageChange,
    emptyMessage = 'No records found',
    highlightSearch = true,
    serverSide = false,
    totalItems = 0,
    totalPages: propTotalPages = 0,
    page: propPage = 1
}) => {
    const [currentPage, setCurrentPage] = useState(propPage);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        setCurrentPage(propPage);
    }, [propPage]);

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((o, p) => (o || {})[p], obj);
    };

    const processedData = useMemo(() => {
        if (serverSide) return data;

        let filteredData = [...data];

        // Apply search filter if searchTerm exists
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredData = filteredData.filter(row =>
                columns.some(col => {
                    const value = col.key.includes('.')
                        ? getNestedValue(row, col.key)
                        : row[col.key];
                    return String(value).toLowerCase().includes(lowerSearchTerm);
                })
            );
        }

        // Apply sorting if sortConfig exists
        if (sortConfig.key) {
            filteredData.sort((a, b) => {
                const aValue = getNestedValue(a, sortConfig.key);
                const bValue = getNestedValue(b, sortConfig.key);

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    }, [data, sortConfig, searchTerm, columns, serverSide]);

    // Use prop value or calculate internally
    const totalPages = serverSide ? propTotalPages : Math.ceil(processedData.length / pageSize);
    const paginatedData = (pagination && !serverSide)
        ? processedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : processedData;

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort className="text-muted" />;
        return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    const highlightText = (text, search) => {
        if (!search || !highlightSearch) return text;

        const parts = String(text).split(new RegExp(`(${search})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === search.toLowerCase()
                ? <mark key={i}>{part}</mark>
                : part
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        if (onPageChange) onPageChange(page);
    };

    return (
        <div className={`bootstrap-data-table ${className}`}>
            {searchable && (
                <div className="mb-3">
                    <InputGroup className="data-table-search">
                        <Form.Control
                            type="search"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => {
                                onSearchChange(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="search-input"
                        />
                        <InputGroup.Text className="search-icon">
                            <FaSearch />
                        </InputGroup.Text>
                    </InputGroup>
                </div>
            )}

            {/* Table */}
            <div className={responsive ? "table-responsive" : ""}>
                <Table
                    striped={striped}
                    hover={hover}
                    bordered={bordered}
                    className="data-table"
                >
                    <thead className="table-header">
                        <tr>
                            {columns.map((col, idx) => (
                                <th
                                    key={idx}
                                    onClick={() => col.sortable !== false && requestSort(col.key)}
                                    style={{
                                        cursor: col.sortable !== false ? 'pointer' : 'default',
                                        width: col.width || 'auto'
                                    }}
                                    className="table-header-cell"
                                >
                                    <div className="d-flex align-items-center">
                                        {col.label}
                                        {col.sortable !== false && (
                                            <span className="ms-2">
                                                {getSortIcon(col.key)}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                            {actions && actions.length > 0 && (
                                <th className="actions-header">Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => (
                                <tr key={index} className="table-row">
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex} className="table-cell">
                                            {col.render
                                                ? col.render(
                                                    col.key.includes('.')
                                                        ? getNestedValue(row, col.key)
                                                        : row[col.key],
                                                    row,
                                                    index
                                                )
                                                : highlightText(
                                                    col.key.includes('.')
                                                        ? getNestedValue(row, col.key)
                                                        : row[col.key],
                                                    searchTerm
                                                )}
                                        </td>
                                    ))}
                                    {actions && actions.length > 0 && (
                                        <td className="actions-cell">
                                            <div className="d-flex gap-2 flex-wrap">
                                                {actions.map((action, actionIndex) => (
                                                    (!action.show || (typeof action.show === 'function' && action.show(row))) && (
                                                        <Button
                                                            key={actionIndex}
                                                            variant={action.variant || 'outline-primary'}
                                                            size="sm"
                                                            onClick={() => action.onClick(row)}
                                                            title={action.tooltip || action.label}
                                                            disabled={action.disabled}
                                                            className="action-button"
                                                        >
                                                            {action.icon || action.label}
                                                            {action.label && action.icon && (
                                                                <span className="ms-1">{action.label}</span>
                                                            )}
                                                        </Button>
                                                    )
                                                ))}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr className="empty-row">
                                <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-4">
                                    <div className="empty-message">
                                        {emptyMessage}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {pagination && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                    <div className="text-muted mb-2 mb-md-0">
                        Showing <strong>{paginatedData.length}</strong> of <strong>{processedData.length}</strong> records
                    </div>
                    <Pagination className="mb-0">
                        <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                        />

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <Pagination.Item
                                    key={pageNum}
                                    active={pageNum === currentPage}
                                    onClick={() => handlePageChange(pageNum)}
                                >
                                    {pageNum}
                                </Pagination.Item>
                            );
                        })}

                        {totalPages > 5 && currentPage < totalPages - 2 && (
                            <Pagination.Ellipsis disabled />
                        )}

                        <Pagination.Next
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default DataTable;